// lib/layer-raster-client.ts
import { computeLayerRaster } from '#lib/layer-cut-utils';
import { copyPixels, decodeWorkerPixels, sendWorkerRequest, toImageData, type WirePixels } from '#lib/worker-rpc';

type LayerRasterJob = Parameters<typeof computeLayerRaster>[1];

interface LayerRasterWorkerRequest extends WirePixels {
  messageId: string;
  job: LayerRasterJob;
}

type LayerRasterWorkerResponse =
  | { messageId: string; error: string }
  | { messageId: string; result: WirePixels | null };

export const handleLayerRasterWorkerRequest = (
  request: LayerRasterWorkerRequest,
): { response: LayerRasterWorkerResponse; transfer: Transferable[] } => {
  try {
    const result = computeLayerRaster(toImageData(request), request.job);
    if (!result) return { response: { messageId: request.messageId, result: null }, transfer: [] };
    const buffer = result.data.buffer as ArrayBuffer;
    return {
      response: {
        messageId: request.messageId,
        result: { buffer, width: result.width, height: result.height },
      },
      transfer: [buffer],
    };
  } catch (error) {
    return {
      response: { messageId: request.messageId, error: error instanceof Error ? error.message : 'Unknown layer raster worker error.' },
      transfer: [],
    };
  }
};

/** The source is copied, never transferred: it stays the undo state and the fallback input. */
// No timeout: a worker that failed to load never answers, so the promise hangs instead of falling back.
export const runLayerRasterInWorker = (
  worker: Worker,
  sourceImageData: ImageData,
  job: LayerRasterJob,
): Promise<ImageData | null> => {
  const source = copyPixels(sourceImageData);
  return sendWorkerRequest<Omit<LayerRasterWorkerRequest, 'messageId'>, WirePixels | null>(
    worker,
    { ...source, job },
    [source.buffer],
    { label: 'Layer raster', errorEventMarksUnavailable: false },
  ).then((result) => result && decodeWorkerPixels('Layer raster', result));
};
